import { useState, useEffect } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Mail, Inbox, Send, FileText, Trash2, Star, RefreshCw, Plus,
  Search, MoreVertical, Paperclip, ChevronLeft, Clock, Check,
  AlertTriangle, Settings, Link2, X, Eye, EyeOff
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

import { API_URL } from '../../config/api';

export default function EmailPage() {
  const { user, isAssistant } = useOutletContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [emails, setEmails] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [showCompose, setShowCompose] = useState(false);
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [activeFolder, setActiveFolder] = useState('inbox');
  const [stats, setStats] = useState({ total: 0, unread: 0 });

  // Handle Google OAuth callback messages
  useEffect(() => {
    const success = searchParams.get('success');
    const error = searchParams.get('error');
    
    if (success === 'gmail_connected') {
      toast.success('Compte Gmail connecté avec succès !');
      setSearchParams({});
      fetchAccounts();
    } else if (error) {
      toast.error(`Erreur de connexion: ${error}`);
      setSearchParams({});
    }
  }, [searchParams]);

  useEffect(() => {
    fetchAccounts();
    fetchDrafts();
  }, []);

  useEffect(() => {
    if (selectedAccount) {
      fetchEmails();
      fetchStats();
    }
  }, [selectedAccount, activeFolder]);

  const fetchAccounts = async () => {
    const token = localStorage.getItem('client_token');
    try {
      const response = await fetch(`${API_URL}/api/email/accounts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAccounts(data);
        if (data.length > 0 && !selectedAccount) {
          setSelectedAccount(data[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching accounts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmails = async () => {
    if (!selectedAccount) return;
    
    const token = localStorage.getItem('client_token');
    setSyncing(true);
    try {
      const response = await fetch(
        `${API_URL}/api/email/accounts/${selectedAccount.id}/emails?folder=INBOX&limit=50`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setEmails(data.emails || []);
      }
    } catch (error) {
      toast.error('Erreur de synchronisation');
    } finally {
      setSyncing(false);
    }
  };

  const fetchStats = async () => {
    if (!selectedAccount) return;
    
    const token = localStorage.getItem('client_token');
    try {
      const response = await fetch(
        `${API_URL}/api/email/accounts/${selectedAccount.id}/stats`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        setStats(await response.json());
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchDrafts = async () => {
    const token = localStorage.getItem('client_token');
    try {
      const response = await fetch(`${API_URL}/api/email/drafts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setDrafts(await response.json());
      }
    } catch (error) {
      console.error('Error fetching drafts:', error);
    }
  };

  const handleMarkAsRead = async (emailUid) => {
    const token = localStorage.getItem('client_token');
    try {
      await fetch(
        `${API_URL}/api/email/accounts/${selectedAccount.id}/emails/${emailUid}/read`,
        { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } }
      );
      setEmails(prev => prev.map(e => 
        e.email_uid === emailUid ? { ...e, is_read: true } : e
      ));
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const handleApproveDraft = async (emailId) => {
    const token = localStorage.getItem('client_token');
    try {
      const response = await fetch(`${API_URL}/api/email/drafts/${emailId}/approve`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Email approuvé et envoyé !');
        fetchDrafts();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Erreur');
      }
    } catch (error) {
      toast.error('Erreur lors de l\'approbation');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays < 7) {
      return date.toLocaleDateString('fr-FR', { weekday: 'short' });
    }
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  const pendingApproval = drafts.filter(d => d.needs_approval);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-rm-primary"></div>
      </div>
    );
  }

  // No accounts connected
  if (accounts.length === 0) {
    return (
      <div className="space-y-6">
        <div className="text-center py-16">
          <Mail className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-rm-text mb-2">
            Connecte ta boîte mail
          </h2>
          <p className="text-rm-text-secondary mb-6 max-w-md mx-auto">
            {isAssistant 
              ? "Le client n'a pas encore connecté de compte email"
              : "Connecte ton compte email pour que ton assistant puisse gérer tes emails"
            }
          </p>
          {!isAssistant && (
            <Button onClick={() => setShowConnectModal(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Connecter un compte email
            </Button>
          )}
        </div>

        {showConnectModal && (
          <ConnectEmailModal 
            onClose={() => setShowConnectModal(false)}
            onSuccess={() => {
              setShowConnectModal(false);
              fetchAccounts();
            }}
          />
        )}
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-120px)] flex flex-col" data-testid="email-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Mail className="w-6 h-6 text-rm-primary" />
          <h1 className="text-xl font-bold text-rm-text">Emails</h1>
          {selectedAccount && (
            <span className="text-sm text-slate-500">{selectedAccount.email_address}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={fetchEmails}
            disabled={syncing}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
          <Button size="sm" onClick={() => setShowCompose(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Nouveau
          </Button>
          {!isAssistant && (
            <Button variant="ghost" size="sm" onClick={() => setShowConnectModal(true)}>
              <Settings className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Pending Approval Banner */}
      {pendingApproval.length > 0 && !isAssistant && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              <span className="text-sm font-medium text-amber-800">
                {pendingApproval.length} email{pendingApproval.length > 1 ? 's' : ''} en attente de validation
              </span>
            </div>
            <Button 
              size="sm" 
              variant="outline"
              className="text-amber-700 border-amber-300"
              onClick={() => setActiveFolder('pending')}
            >
              Voir
            </Button>
          </div>
        </motion.div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex gap-4 min-h-0">
        {/* Sidebar */}
        <div className="w-48 flex-shrink-0 space-y-1">
          <button
            onClick={() => setActiveFolder('inbox')}
            className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-left transition-colors ${
              activeFolder === 'inbox' ? 'bg-rm-primary/10 text-rm-primary' : 'hover:bg-slate-100'
            }`}
          >
            <span className="flex items-center gap-2">
              <Inbox className="w-4 h-4" />
              Boîte de réception
            </span>
            {stats.unread > 0 && (
              <span className="text-xs bg-rm-primary text-white px-2 py-0.5 rounded-full">
                {stats.unread}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveFolder('drafts')}
            className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-left transition-colors ${
              activeFolder === 'drafts' ? 'bg-rm-primary/10 text-rm-primary' : 'hover:bg-slate-100'
            }`}
          >
            <span className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Brouillons
            </span>
            {drafts.filter(d => !d.needs_approval).length > 0 && (
              <span className="text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full">
                {drafts.filter(d => !d.needs_approval).length}
              </span>
            )}
          </button>
          {pendingApproval.length > 0 && (
            <button
              onClick={() => setActiveFolder('pending')}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-left transition-colors ${
                activeFolder === 'pending' ? 'bg-amber-100 text-amber-700' : 'hover:bg-slate-100'
              }`}
            >
              <span className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                À valider
              </span>
              <span className="text-xs bg-amber-500 text-white px-2 py-0.5 rounded-full">
                {pendingApproval.length}
              </span>
            </button>
          )}
          <button
            onClick={() => setActiveFolder('sent')}
            className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left transition-colors ${
              activeFolder === 'sent' ? 'bg-rm-primary/10 text-rm-primary' : 'hover:bg-slate-100'
            }`}
          >
            <Send className="w-4 h-4" />
            Envoyés
          </button>
        </div>

        {/* Email List */}
        <div className="flex-1 flex min-h-0 bg-white rounded-xl border border-slate-200 overflow-hidden">
          {/* List */}
          <div className={`${selectedEmail ? 'w-1/3 border-r' : 'w-full'} overflow-y-auto`}>
            {activeFolder === 'inbox' && (
              emails.length === 0 ? (
                <div className="text-center py-12">
                  <Inbox className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">Aucun email</p>
                </div>
              ) : (
                emails.map((email) => (
                  <div
                    key={email.email_uid}
                    onClick={() => {
                      setSelectedEmail(email);
                      if (!email.is_read) handleMarkAsRead(email.email_uid);
                    }}
                    className={`p-3 border-b border-slate-100 cursor-pointer hover:bg-slate-50 transition-colors ${
                      !email.is_read ? 'bg-blue-50/50' : ''
                    } ${selectedEmail?.email_uid === email.email_uid ? 'bg-rm-primary/5' : ''}`}
                  >
                    <div className="flex items-start gap-2">
                      <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
                        !email.is_read ? 'bg-rm-primary' : 'bg-transparent'
                      }`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className={`text-sm truncate ${!email.is_read ? 'font-semibold' : ''}`}>
                            {email.from_name || email.from_address}
                          </span>
                          <span className="text-xs text-slate-400 flex-shrink-0">
                            {formatDate(email.received_at)}
                          </span>
                        </div>
                        <p className={`text-sm truncate ${!email.is_read ? 'font-medium text-rm-text' : 'text-slate-600'}`}>
                          {email.subject}
                        </p>
                        <p className="text-xs text-slate-400 truncate mt-0.5">
                          {email.snippet}
                        </p>
                        {email.attachments?.length > 0 && (
                          <div className="flex items-center gap-1 mt-1">
                            <Paperclip className="w-3 h-3 text-slate-400" />
                            <span className="text-xs text-slate-400">
                              {email.attachments.length} pièce(s) jointe(s)
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )
            )}

            {activeFolder === 'pending' && (
              pendingApproval.length === 0 ? (
                <div className="text-center py-12">
                  <Check className="w-12 h-12 text-green-300 mx-auto mb-3" />
                  <p className="text-slate-500">Aucun email en attente</p>
                </div>
              ) : (
                pendingApproval.map((draft) => (
                  <div
                    key={draft.id}
                    onClick={() => setSelectedEmail(draft)}
                    className={`p-3 border-b border-slate-100 cursor-pointer hover:bg-slate-50 transition-colors ${
                      selectedEmail?.id === draft.id ? 'bg-amber-50' : ''
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      <Clock className="w-4 h-4 text-amber-500 mt-1 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm font-medium truncate">
                            → {draft.to_addresses?.[0]}
                          </span>
                          <span className="text-xs text-slate-400">
                            {formatDate(draft.created_at)}
                          </span>
                        </div>
                        <p className="text-sm text-slate-600 truncate">{draft.subject}</p>
                        <p className="text-xs text-amber-600 mt-1">
                          Par {draft.created_by_name}
                        </p>
                      </div>
                    </div>
                  </div>
                ))
              )
            )}

            {activeFolder === 'drafts' && (
              drafts.filter(d => !d.needs_approval).length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">Aucun brouillon</p>
                </div>
              ) : (
                drafts.filter(d => !d.needs_approval).map((draft) => (
                  <div
                    key={draft.id}
                    onClick={() => setSelectedEmail(draft)}
                    className={`p-3 border-b border-slate-100 cursor-pointer hover:bg-slate-50 transition-colors ${
                      selectedEmail?.id === draft.id ? 'bg-rm-primary/5' : ''
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">→ {draft.to_addresses?.[0]}</p>
                      <p className="text-sm text-slate-600 truncate">{draft.subject}</p>
                    </div>
                  </div>
                ))
              )
            )}
          </div>

          {/* Email Detail */}
          {selectedEmail && (
            <div className="flex-1 flex flex-col overflow-hidden">
              <div className="p-4 border-b border-slate-100 flex items-center justify-between">
                <button
                  onClick={() => setSelectedEmail(null)}
                  className="p-2 hover:bg-slate-100 rounded-lg lg:hidden"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <h3 className="font-semibold text-rm-text truncate flex-1">
                  {selectedEmail.subject}
                </h3>
                {selectedEmail.needs_approval && !isAssistant && (
                  <Button 
                    size="sm"
                    onClick={() => handleApproveDraft(selectedEmail.id)}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <Check className="w-4 h-4 mr-2" />
                    Approuver et envoyer
                  </Button>
                )}
              </div>
              <div className="p-4 border-b border-slate-100 bg-slate-50">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-rm-primary/10 rounded-full flex items-center justify-center">
                    <span className="text-rm-primary font-semibold">
                      {(selectedEmail.from_name || selectedEmail.from_address || selectedEmail.to_addresses?.[0])?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium text-rm-text">
                      {selectedEmail.is_outgoing 
                        ? `À: ${selectedEmail.to_addresses?.join(', ')}`
                        : selectedEmail.from_name || selectedEmail.from_address
                      }
                    </p>
                    <p className="text-xs text-slate-500">
                      {selectedEmail.received_at 
                        ? new Date(selectedEmail.received_at).toLocaleString('fr-FR')
                        : new Date(selectedEmail.created_at).toLocaleString('fr-FR')
                      }
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex-1 p-4 overflow-y-auto">
                {selectedEmail.body_html ? (
                  <div 
                    className="prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: selectedEmail.body_html }}
                  />
                ) : (
                  <pre className="whitespace-pre-wrap font-sans text-sm text-slate-700">
                    {selectedEmail.body_text}
                  </pre>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Compose Modal */}
      {showCompose && selectedAccount && (
        <ComposeEmailModal
          account={selectedAccount}
          isAssistant={isAssistant}
          onClose={() => setShowCompose(false)}
          onSent={() => {
            setShowCompose(false);
            fetchDrafts();
            toast.success(isAssistant ? 'Email envoyé pour validation' : 'Email envoyé !');
          }}
        />
      )}

      {/* Connect Account Modal */}
      {showConnectModal && (
        <ConnectEmailModal 
          onClose={() => setShowConnectModal(false)}
          onSuccess={() => {
            setShowConnectModal(false);
            fetchAccounts();
          }}
        />
      )}
    </div>
  );
}

// Compose Email Modal Component
function ComposeEmailModal({ account, isAssistant, onClose, onSent }) {
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!to || !subject) {
      toast.error('Destinataire et sujet requis');
      return;
    }

    const token = localStorage.getItem('client_token');
    setSending(true);

    try {
      const response = await fetch(`${API_URL}/api/email/accounts/${account.id}/send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          account_id: account.id,
          to_addresses: to.split(',').map(e => e.trim()),
          subject,
          body_text: body,
          send_immediately: true
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        onSent();
      } else {
        toast.error(data.detail || 'Erreur');
      }
    } catch (error) {
      toast.error('Erreur d\'envoi');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-white rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden"
      >
        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-semibold text-rm-text">Nouveau message</h3>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <input
              type="text"
              placeholder="À: destinataire@email.com"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="w-full px-3 py-2 border-b border-slate-200 focus:border-rm-primary focus:outline-none"
            />
          </div>
          <div>
            <input
              type="text"
              placeholder="Objet"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full px-3 py-2 border-b border-slate-200 focus:border-rm-primary focus:outline-none"
            />
          </div>
          <div>
            <textarea
              placeholder="Votre message..."
              value={body}
              onChange={(e) => setBody(e.target.value)}
              className="w-full px-3 py-2 h-64 resize-none focus:outline-none"
            />
          </div>
        </div>
        <div className="p-4 border-t border-slate-100 flex items-center justify-between">
          <p className="text-xs text-slate-500">
            De: {account.email_address}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Annuler</Button>
            <Button onClick={handleSend} disabled={sending}>
              {sending ? (
                <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white" />
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  {isAssistant && account.assistant_requires_approval 
                    ? 'Demander validation' 
                    : 'Envoyer'
                  }
                </>
              )}
            </Button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

// Connect Email Modal Component
function ConnectEmailModal({ onClose, onSuccess }) {
  const [connectionMethod, setConnectionMethod] = useState(null); // null | 'google' | 'imap'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [imapServer, setImapServer] = useState('');
  const [smtpServer, setSmtpServer] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const handleGoogleConnect = async () => {
    const token = localStorage.getItem('client_token');
    setConnecting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/oauth/google/authorize?service=gmail`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Redirect to Google OAuth
        window.location.href = data.authorization_url;
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Erreur de connexion Google');
      }
    } catch (error) {
      toast.error('Erreur de connexion Google');
    } finally {
      setConnecting(false);
    }
  };

  const handleImapConnect = async () => {
    if (!email || !password) {
      toast.error('Email et mot de passe requis');
      return;
    }

    const token = localStorage.getItem('client_token');
    setConnecting(true);

    try {
      const response = await fetch(`${API_URL}/api/email/accounts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          provider: 'imap',
          email_address: email,
          password,
          imap_server: imapServer || undefined,
          smtp_server: smtpServer || undefined
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        toast.success('Compte connecté !');
        onSuccess();
      } else {
        toast.error(data.detail || 'Erreur de connexion');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setConnecting(false);
    }
  };

  // Method selection screen
  if (!connectionMethod) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden"
        >
          <div className="p-4 border-b border-slate-100 flex items-center justify-between">
            <h3 className="font-semibold text-rm-text flex items-center gap-2">
              <Link2 className="w-5 h-5 text-rm-primary" />
              Connecter un compte email
            </h3>
            <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <div className="p-4 space-y-3">
            <p className="text-sm text-slate-600 text-center mb-4">
              Choisis ta méthode de connexion
            </p>
            
            {/* Google OAuth Button */}
            <button
              onClick={handleGoogleConnect}
              disabled={connecting}
              className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-white border-2 border-slate-200 rounded-xl hover:border-slate-300 hover:bg-slate-50 transition-all group"
              data-testid="connect-google-email-btn"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              <span className="font-medium text-slate-700">Continuer avec Google</span>
              {connecting && <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-slate-400" />}
            </button>
            
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200"></div>
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="px-2 bg-white text-slate-400">ou</span>
              </div>
            </div>
            
            {/* IMAP Manual Connection */}
            <button
              onClick={() => setConnectionMethod('imap')}
              className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-slate-100 border border-slate-200 rounded-xl hover:bg-slate-200 transition-all"
              data-testid="connect-imap-email-btn"
            >
              <Mail className="w-5 h-5 text-slate-600" />
              <span className="font-medium text-slate-700">Connexion manuelle (IMAP)</span>
            </button>
            
            <p className="text-xs text-slate-500 text-center mt-4">
              La connexion Google est recommandée pour Gmail
            </p>
          </div>
        </motion.div>
      </div>
    );
  }

  // IMAP configuration screen
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden"
      >
        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button 
              onClick={() => setConnectionMethod(null)}
              className="p-1 hover:bg-slate-100 rounded"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <h3 className="font-semibold text-rm-text">
              Connexion IMAP/SMTP
            </h3>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-4 space-y-4">
          <div className="bg-blue-50 rounded-lg p-3 text-sm text-blue-700">
            <p className="font-medium mb-1">Fournisseurs supportés automatiquement :</p>
            <p className="text-xs">Gmail, Outlook, Yahoo, Orange, Free, SFR...</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Adresse email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="ton@email.com"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-rm-primary focus:border-rm-primary"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Mot de passe
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-rm-primary focus:border-rm-primary pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Pour Gmail, utilise un "mot de passe d'application"
            </p>
          </div>

          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="text-sm text-rm-primary hover:underline flex items-center gap-1"
          >
            <Settings className="w-3 h-3" />
            {showAdvanced ? 'Masquer' : 'Configuration avancée'}
          </button>

          {showAdvanced && (
            <div className="space-y-3 p-3 bg-slate-50 rounded-lg">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Serveur IMAP
                </label>
                <input
                  type="text"
                  value={imapServer}
                  onChange={(e) => setImapServer(e.target.value)}
                  placeholder="imap.fournisseur.com"
                  className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Serveur SMTP
                </label>
                <input
                  type="text"
                  value={smtpServer}
                  onChange={(e) => setSmtpServer(e.target.value)}
                  placeholder="smtp.fournisseur.com"
                  className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded"
                />
              </div>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-slate-100 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={handleImapConnect} disabled={connecting}>
            {connecting ? (
              <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white" />
            ) : (
              <>
                <Link2 className="w-4 h-4 mr-2" />
                Connecter
              </>
            )}
          </Button>
        </div>
      </motion.div>
    </div>
  );
}
